import React, { useCallback } from "react";
import { commitMutation, graphql, useFragment } from "react-relay";

import { faCheckCircle, faCheckSquare } from "@awesome.me/kit-4c83d50d0d/icons/classic/regular";

import { environment } from "~/models";
import { toast } from "~/ui";
import Menu from "~/ui/menu";

import { MenuItemTooltip } from "./MenuItemTooltip";

import type { CarryForward_MoreActionsMenu_note$key } from "~/__generated__/CarryForward_MoreActionsMenu_note.graphql";
import type { CarryForwardBlocksMutation } from "~/__generated__/CarryForwardBlocksMutation.graphql";

const noteFragment = graphql`
	fragment CarryForward_MoreActionsMenu_note on Note {
		id
		previousNote {
			pendingAgendaCount
			pendingTodoCount
		}
	}
`;

const carryForwardBlocksMutation = graphql`
	mutation CarryForwardBlocksMutation($noteId: ID!, $type: String!) {
		noteCarryForwardBlocks(noteId: $noteId, type: $type) {
			note {
				actionItemsCarriedForward
				talkingPointsCarriedForward
			}
		}
	}
`;

function useCarryForwardBlocks(noteId: string, type: "talking-point" | "action-item") {
	return useCallback(() => {
		commitMutation<CarryForwardBlocksMutation>(environment, {
			mutation: carryForwardBlocksMutation,
			variables: {
				noteId,
				type,
			},
			onCompleted: ({ noteCarryForwardBlocks }, err) => {
				if (err?.length || !noteCarryForwardBlocks) toast.error("Failed to carry forward items", { title: "Error" });
			},
		});
	}, [noteId, type]);
}

interface CarryForwardMenuItemProps {
	note: CarryForward_MoreActionsMenu_note$key;
}

export function CarryForwardTalkingPoints({ note: noteKey }: CarryForwardMenuItemProps) {
	const note = useFragment(noteFragment, noteKey);
	const noteId = note.id;
	const disabled = !Boolean(note.previousNote?.pendingAgendaCount);

	const carryForwardTalkingPoints = useCarryForwardBlocks(noteId, "talking-point");

	return (
		<Menu.Item icon={faCheckCircle} onSelect={carryForwardTalkingPoints} disabled={disabled}>
			<MenuItemTooltip
				disabled={!disabled}
				content="No talking points to carry forward"
				data-test-id="note-header-dropdown-carryforwardtp-item"
			>
				<span>Carry forward talking points</span>
			</MenuItemTooltip>
		</Menu.Item>
	);
}

export function CarryForwardActionItems({ note: noteKey }: CarryForwardMenuItemProps) {
	const note = useFragment(noteFragment, noteKey);
	const noteId = note.id;
	const disabled = !Boolean(note.previousNote?.pendingTodoCount);

	const carryForwardActionItems = useCarryForwardBlocks(noteId, "action-item");

	return (
		<Menu.Item
			icon={faCheckSquare}
			onSelect={carryForwardActionItems}
			disabled={disabled}
			data-test-id="note-header-dropdown-carryforwardai-item"
		>
			<MenuItemTooltip disabled={!disabled} content="No action items to carry forward">
				<span>Carry forward action items</span>
			</MenuItemTooltip>
		</Menu.Item>
	);
}
